/* Travel Policy Cancellation */

import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import TravelCancellationReviewPage from "./travelCancellationReviewPage";
import {
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW,
} from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";
import TravelCancellationReasonPage from "./travelCancellationReasonPage";
import TravelConfirmCancellationPage from "./travelConfirmCancellationPage";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetPolicyCancellationFlow } from "../../../../redux/features/motorInsurancePolicyCancellationFlowSlice";
import { toast } from "sonner";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
} from "../../../../redux/features/policyDetailsSlice";
import { createCaseComplaint } from "../../../../redux/features/crm/caseComplaintSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { policyView } from "../../../../redux/features/policyDetailsSlice";
import CryptoJS from "crypto-js";

const CancelPolicyTravel = () => {
  const { currentStep } = usePolicyCancellationFlow();
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { policy_id } = useParams();

  const [selectedReasonOut, setSelectedReasonOut] = useState("");

  // Decode policy number from URL
  const policyNumber = decodeURIComponent(policy_id);

  useEffect(() => {
    dispatch(resetPolicyCancellationFlow());
    
    // Load travel policy data if not already loaded
    const polReq = {
      PolicyNumber: policyNumber,
      Channel: getClientSource(),
    };
    dispatch(policyView(polReq));
  }, [dispatch, policyNumber]);

  const handleNavigationToPolicyOverview = () => {
    // We need to recreate the encrypted policy ID to navigate back
    const secretKey = "i-hate-meej";
    const encryptedValue = CryptoJS.AES.encrypt(policyNumber, secretKey).toString();
    
    // Convert to URL-safe format (same as policy card)
    const urlSafeEncryptedValue = encryptedValue
      .split("+")
      .join("-") // Replace '+' with '-'
      .split("/")
      .join("_") // Replace '/' with '_'
      .split("=")
      .join(""); // Remove '=' padding
    
    navigate(`/travel-insurance/policy-overview/${urlSafeEncryptedValue}?tabType=ManagePolicy`);
  };

  // Get policy detail. Call API if empty or check redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);
  const profileInfo = useSelector(selectProfileData);

  // Debug: Log all available data to see what we have for travel
  console.log("=== TRAVEL POLICY DATA DEBUG ===");
  console.log("policyDetailsInfo:", policyDetailsInfo);
  console.log("policyDetailsPartyInfo:", policyDetailsPartyInfo);
  console.log("profileInfo:", profileInfo);
  console.log("policyNumber from URL:", policyNumber);
  console.log("=== END DEBUG ===");

  const confimedCancellation = async () => {
    const payload = {
      FirstName: policyDetailsPartyInfo?.FirstName || profileInfo?.FirstName || "",
      LastName: policyDetailsPartyInfo?.LastName || profileInfo?.LastName || "",
      PhoneNumber: policyDetailsPartyInfo?.PhoneNumber || profileInfo?.PhoneNumber || "",
      Email: profileInfo?.Email || "",
      Subject: "Cancel Policy",
      Product: "Travel Guard", // Travel insurance
      Lob: "PERLINE", // Travel → PERLINE as specified
      Descreption: `Policy to be cancelled. \nSelected Reason: ${selectedReasonOut}`,
      ClaimNumber: "",
      PolicyNumber: policyNumber,
      VNumber: "", // Empty string for travel (not motor)
      Channel: getClientSource(),
    };

    // Calling API case/complaint
    try {
      const caseComplaintAction = await dispatch(createCaseComplaint(payload));
      const caseComplaintResult = unwrapResult(caseComplaintAction);

      if (caseComplaintResult) {
        handleNavigationToPolicyOverview();
        toast.success(
          translate(
            "Your request for cancellation has been successfully sent",
            "تم إرسال طلب الإلغاء بنجاح"
          )
        );
      }
    } catch (error) {
      toast.error(
        translate(
          "Error in sending cancel policy request",
          "خطأ في إرسال طلب إلغاء السياسة"
        )
      );
    }
  };

  // Render page based on current step
  const renderPage = () => {
    switch (currentStep?.name) {
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW:
        return (
          <TravelCancellationReviewPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON:
        return (
          <TravelCancellationReasonPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            setSelectedReasonOut={setSelectedReasonOut}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION:
        return (
          <TravelConfirmCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            confimedCancellation={confimedCancellation}
            selectedReasonOut={selectedReasonOut}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[7%] ipad:h-[93vh] ipad:overflow-y-scroll">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={currentStep?.name || "step"}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          {renderPage()}
        </motion.div>
      </AnimatePresence>
      <div id="travel-footer-portal" />
      <div id="home-footer-portal" />
    </div>
  );
};

export default CancelPolicyTravel;